<template>
  <b-modal v-model="localVisible" title="Formatting Rules" @ok="onSave" @hide="onHide" size="xl">
    <template #title>
      Formatting Rules
      <modal-help topic="formatting-rules" />
    </template>

    <!-- Tab bar -->
    <div class="re-tabs">
      <button class="re-tab" :class="{ active: activeTab === 'visual' }" @click="activeTab = 'visual'">Visual Formatting</button>
      <button class="re-tab" :class="{ active: activeTab === 'value' }" @click="activeTab = 'value'">Value Formatting</button>
    </div>

    <!-- ═══════════ VISUAL FORMATTING TAB ═══════════ -->
    <div v-if="activeTab === 'visual'" class="re-container">
      <!-- Rule chain display -->
      <div v-if="localRules.length" class="re-rule-list">
        <template v-for="(rule, idx) in localRules" :key="rule.id">
          <div v-if="idx > 0" class="re-connector">
            <span class="re-connector-line"></span>
          </div>
          <div class="re-rule-item" :class="{ 're-rule-editing': editingIndex === idx }">
            <button type="button" class="re-rule-open" @click="editRule(idx)">
            <span class="re-type-badge" :class="'re-type-' + rule.type">{{ rule.type.toUpperCase() }}</span>
            <span class="re-rule-body">
              <template v-if="rule.type !== 'else'">
                <span class="re-rule-conditions">
                  <template v-for="(cond, ci) in rule.conditions" :key="ci">
                    <span v-if="ci > 0" class="re-cond-join">{{ rule.conditionMatchMode === 'any' ? 'OR' : 'AND' }}</span>
                    <span class="re-cond-text">{{ conditionSummary(cond) }}</span>
                  </template>
                  <span v-if="!rule.conditions || !rule.conditions.length" class="re-cond-empty">(no conditions)</span>
                </span>
              </template>
              <span class="re-rule-arrow">&rarr;</span>
              <span class="re-rule-actions">
                <span v-for="(action, ai) in rule.actions" :key="ai" class="re-action-pill" :style="actionPillStyle(action)">
                  {{ actionSummary(action) }}
                </span>
                <span v-if="!rule.actions || !rule.actions.length" class="re-action-empty">no actions</span>
              </span>
            </span>
            </button>
            <button type="button" class="re-rule-remove" @click.stop="removeRule(idx)" aria-label="Remove rule">&times;</button>
          </div>
        </template>
      </div>

      <!-- Empty state -->
      <div v-if="!localRules.length && !isEditing" class="re-empty">
        No visual formatting rules configured
      </div>

      <!-- Edit form -->
      <div v-if="isEditing" class="re-edit-form">
        <div class="re-edit-title">{{ editingIndex !== null ? 'Edit' : 'New' }} Rule</div>

        <!-- Rule type: hidden when the chain leaves only one choice (a lone IF, or ELSE IF after ELSE) -->
        <div v-if="availableTypes.length > 1" class="re-form-row">
          <label class="re-form-label">Rule Type</label>
          <select v-model="editType" class="re-select">
            <option v-for="opt in availableTypes" :key="opt" :value="opt">{{ opt.toUpperCase() }}</option>
          </select>
        </div>

        <!-- Conditions (not for else) -->
        <template v-if="editType !== 'else'">
          <div class="re-form-section">
            <div class="re-section-header">
              <span class="re-section-title">Conditions</span>
              <div v-if="editConditions.length > 1" class="re-match-toggle">
                <button class="re-mt-btn" :class="{ active: editMatchMode === 'all' }" @click="editMatchMode = 'all'">ALL</button>
                <button class="re-mt-btn" :class="{ active: editMatchMode === 'any' }" @click="editMatchMode = 'any'">ANY</button>
              </div>
            </div>

            <div v-for="(cond, ci) in editConditions" :key="ci" class="re-condition-row">
              <select v-model="cond.source" @change="onCondSourceChange(cond)" class="re-select re-select-sm">
                <option value="self">This cell</option>
                <option value="field">Another field</option>
                <option value="related">Related field</option>
              </select>

              <select v-if="cond.source === 'field'" v-model="cond.field" @change="onCondFieldChange(cond)" class="re-select re-select-sm">
                <option value="" disabled>Select field...</option>
                <option v-for="col in directColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
              </select>

              <template v-if="cond.source === 'related'">
                <select v-model="cond.relationshipColumn" @change="onCondRelColChange(cond)" class="re-select re-select-sm">
                  <option value="" disabled>Relationship...</option>
                  <option v-for="rel in relationshipColumns" :key="rel.uniqueTitle" :value="rel.uniqueTitle">{{ rel.title || rel.uniqueTitle }}</option>
                </select>
                <select v-if="cond.relationshipColumn" v-model="cond.relatedQuantifier" class="re-select re-select-sm re-quantifier">
                  <option value="any">any</option>
                  <option value="all">all</option>
                  <option value="none">none of</option>
                </select>
                <select v-if="cond.relationshipColumn" v-model="cond.field" class="re-select re-select-sm">
                  <option value="" disabled>Field...</option>
                  <option v-for="col in getRelatedColumns(cond.relationshipColumn)" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
                </select>
              </template>

              <select v-model="cond.operator" class="re-select re-select-sm">
                <option v-for="op in operatorsForDataType(cond.dataType || columnDataType)" :key="op.key" :value="op.key">{{ op.label }}</option>
              </select>

              <template v-if="!isNoValueOp(cond.operator)">
                <!-- Relationship record picker -->
                <template v-if="condFieldIsRelationship(cond)">
                  <div class="re-rel-picker">
                    <div v-if="cond.relSelectedRecords && cond.relSelectedRecords.length" class="re-rel-tags">
                      <span v-for="rec in cond.relSelectedRecords" :key="rec.id" class="re-rel-tag">
                        {{ rec.displayValue }}
                        <button class="re-rel-tag-remove" @click="removeCondRelRecord(cond, rec.id)">&times;</button>
                      </span>
                    </div>
                    <div class="re-rel-search-wrap">
                      <input
                        type="text"
                        v-model="cond.relSearchQuery"
                        class="re-input re-input-sm"
                        placeholder="Search records..."
                        @input="onCondRelSearchInput(cond)"
                      />
                      <div v-if="cond.relSearchLoading" class="re-rel-loading">searching...</div>
                    </div>
                    <div v-if="cond.relSearchResults && cond.relSearchResults.length" class="re-rel-results">
                      <div
                        v-for="rec in cond.relSearchResults"
                        :key="rec.id"
                        class="re-rel-result-item"
                        @click="selectCondRelRecord(cond, rec)"
                      >{{ rec.displayValue || '(no value)' }}</div>
                    </div>
                  </div>
                </template>
                <input
                  v-else-if="cond.dataType === 'number'"
                  v-model.number="cond.value"
                  class="re-input re-input-sm"
                  type="number"
                  step="any"
                  placeholder="0"
                />
                <input
                  v-else-if="cond.dataType === 'datetime'"
                  v-model="cond.value"
                  class="re-input re-input-sm"
                  type="datetime-local"
                />
                <select
                  v-else-if="cond.dataType === 'checkbox'"
                  v-model="cond.value"
                  class="re-select re-select-sm"
                >
                  <option value="true">Checked</option>
                  <option value="false">Unchecked</option>
                </select>
                <input
                  v-else
                  v-model="cond.value"
                  class="re-input re-input-sm"
                  type="text"
                  :placeholder="regexpPlaceholder(cond.operator)"
                />
              </template>

              <button class="re-cond-remove" @click="removeCondition(ci)" title="Remove condition">&times;</button>
            </div>

            <button class="re-add-btn" @click="addCondition">+ add condition</button>
          </div>
        </template>

        <!-- Actions -->
        <div class="re-form-section">
          <div class="re-section-header">
            <span class="re-section-title">Formatting Actions</span>
          </div>

          <div v-for="(action, ai) in editActions" :key="ai" class="re-action-row">
            <select v-model="action.property" @change="onActionPropertyChange(action)" class="re-select re-select-sm">
              <option value="" disabled>Property...</option>
              <option v-for="prop in formattingPropertyOptions" :key="prop.key" :value="prop.key">{{ prop.label }}</option>
            </select>

            <template v-if="action.property">
              <input
                v-if="getPropertyType(action.property) === 'color'"
                type="color"
                v-model="action.value"
                class="re-color-input"
              />
              <label v-else-if="getPropertyType(action.property) === 'boolean'" class="re-checkbox-label">
                <input type="checkbox" v-model="action.value" />
                {{ action.value ? 'Yes' : 'No' }}
              </label>
              <select
                v-else-if="getPropertyType(action.property) === 'select'"
                v-model="action.value"
                class="re-select re-select-sm"
              >
                <option v-for="opt in getPropertyOptions(action.property)" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <input
                v-else-if="getPropertyType(action.property) === 'number'"
                type="number"
                v-model.number="action.value"
                class="re-input re-input-sm"
                placeholder="0"
              />
              <textarea
                v-else-if="action.property === 'customCss'"
                v-model="action.value"
                class="re-textarea"
                placeholder="color: red; font-weight: bold;"
                rows="2"
              />
              <input
                v-else
                type="text"
                v-model="action.value"
                class="re-input re-input-sm"
                placeholder="value..."
              />
            </template>

            <button class="re-action-remove" @click="removeAction(ai)" title="Remove action">&times;</button>
          </div>

          <button class="re-add-btn" @click="addAction">+ add action</button>
        </div>

        <!-- Edit buttons -->
        <div class="re-edit-buttons">
          <button class="re-btn re-btn-save" @click="saveRule">{{ editingIndex !== null ? 'Update' : 'Add' }} Rule</button>
          <button class="re-btn re-btn-cancel" @click="cancelEdit">Cancel</button>
        </div>
      </div>

      <!-- Add rule button -->
      <div v-if="!isEditing" class="re-add-rule-row">
        <button class="re-add-btn" @click="startAddRule">+ add rule</button>
      </div>
    </div>

    <!-- ═══════════ VALUE FORMATTING TAB ═══════════ -->
    <div v-if="activeTab === 'value'" class="re-container">

      <!-- Number formatting (shown for number fields) -->
      <template v-if="columnDataType === 'number'">
        <div class="re-vf-section-title">Number Display</div>

        <div class="re-vf-row">
          <label class="re-form-label">Decimal Places</label>
          <select v-model="localValueFormatting.decimalPlaces" class="re-select">
            <option :value="null">Auto</option>
            <option v-for="n in 11" :key="n - 1" :value="n - 1">{{ n - 1 }}</option>
          </select>
        </div>

        <div class="re-vf-row">
          <label class="re-form-label">Thousands Separator</label>
          <select v-model="localValueFormatting.thousandsSeparator" class="re-select">
            <option :value="null">None</option>
            <option value=",">Comma (1,000)</option>
            <option value=".">Period (1.000)</option>
            <option value=" ">Space (1 000)</option>
          </select>
        </div>

        <div class="re-vf-row">
          <label class="re-form-label">Decimal Separator</label>
          <select v-model="localValueFormatting.decimalSeparator" class="re-select">
            <option value=".">Period (0.50)</option>
            <option value=",">Comma (0,50)</option>
          </select>
        </div>

        <div class="re-vf-row">
          <label class="re-form-label">Currency</label>
          <select v-model="localValueFormatting.currency" class="re-select">
            <option :value="null">None</option>
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (&euro;)</option>
            <option value="GBP">GBP (&pound;)</option>
            <option value="JPY">JPY (&yen;)</option>
            <option value="CAD">CAD (CA$)</option>
            <option value="AUD">AUD (A$)</option>
            <option value="CHF">CHF</option>
            <option value="CNY">CNY (&yen;)</option>
            <option value="INR">INR (&#8377;)</option>
            <option value="BRL">BRL (R$)</option>
          </select>
        </div>

        <div class="re-vf-row">
          <label class="re-form-label">Rounding</label>
          <select v-model="localValueFormatting.rounding" class="re-select">
            <option :value="null">Round</option>
            <option value="floor">Round down</option>
            <option value="ceil">Round up</option>
          </select>
        </div>

        <div class="re-vf-row">
          <label class="re-checkbox-label">
            <input type="checkbox" v-model="localValueFormatting.percentMode" />
            Display as Percentage
          </label>
        </div>

        <!-- Preview -->
        <div class="re-vf-preview">
          <span class="re-vf-preview-label">Preview:</span>
          <span class="re-vf-preview-value">{{ numberPreview }}</span>
        </div>
      </template>

      <!-- Text formatting (shown for text-like fields) -->
      <template v-if="columnDataType === 'string' || columnDataType === 'text'">
        <div class="re-vf-section-title">Text Display</div>

        <div class="re-vf-row">
          <label class="re-form-label">Max Display Length</label>
          <div class="re-vf-inline">
            <input
              type="number"
              v-model.number="localValueFormatting.maxLength"
              class="re-input"
              min="1"
              placeholder="No limit"
            />
            <span class="re-vf-hint">Truncates with ellipsis</span>
          </div>
        </div>

        <div class="re-vf-row">
          <label class="re-checkbox-label">
            <input type="checkbox" v-model="localValueFormatting.stripHtml" />
            Strip HTML Tags
          </label>
        </div>

        <div class="re-vf-row">
          <label class="re-checkbox-label">
            <input type="checkbox" v-model="localValueFormatting.stripNonAlphanumeric" />
            Strip Non-Alphanumeric Characters
          </label>
        </div>

        <div class="re-vf-row">
          <label class="re-checkbox-label">
            <input type="checkbox" v-model="localValueFormatting.trimWhitespace" />
            Trim &amp; Collapse Whitespace
          </label>
        </div>

        <div class="re-vf-row">
          <label class="re-form-label">Case Transform</label>
          <select v-model="localValueFormatting.caseTransform" class="re-select">
            <option :value="null">None</option>
            <option value="uppercase">UPPERCASE</option>
            <option value="lowercase">lowercase</option>
            <option value="capitalize">Capitalize Words</option>
          </select>
        </div>

        <div class="re-vf-row">
          <label class="re-form-label">Font</label>
          <select v-model="localValueFormatting.fontFamily" class="re-select">
            <option :value="null">Default</option>
            <option value="Arial, sans-serif">Arial</option>
            <option value="Georgia, serif">Georgia</option>
            <option value="&quot;Times New Roman&quot;, serif">Times New Roman</option>
            <option value="&quot;Courier New&quot;, monospace">Courier New</option>
            <option value="Verdana, sans-serif">Verdana</option>
          </select>
        </div>
        <div class="re-vf-row">
          <label class="re-form-label">Font size</label>
          <input type="number" v-model.number="localValueFormatting.fontSize" class="re-input" min="8" max="96" placeholder="Default" />
        </div>
        <div class="re-vf-row">
          <label class="re-checkbox-label"><input type="checkbox" v-model="localValueFormatting.bold" /> Bold</label>
        </div>
        <div class="re-vf-row">
          <label class="re-checkbox-label"><input type="checkbox" v-model="localValueFormatting.italic" /> Italic</label>
        </div>
        <div class="re-vf-row">
          <label class="re-checkbox-label"><input type="checkbox" v-model="localValueFormatting.strike" /> Strikethrough</label>
        </div>
        <div class="re-vf-row">
          <label class="re-form-label">Text replacements</label>
          <div v-for="(row, index) in replacementRows" :key="index" class="re-vf-inline">
            <input v-model="row.find" class="re-input" placeholder="find" />
            <input v-model="row.replace" class="re-input" placeholder="replace with" />
            <button type="button" class="re-action-remove" @click="replacementRows.splice(index, 1)">&times;</button>
          </div>
          <button type="button" class="re-add-btn" @click="replacementRows.push({ find: '', replace: '' })">+ add replacement</button>
        </div>
      </template>

      <!-- Unsupported type -->
      <div v-if="columnDataType !== 'number' && columnDataType !== 'string' && columnDataType !== 'text'" class="re-empty">
        Value formatting is available for number and text fields.
      </div>

      <!-- Clear button -->
      <div v-if="columnDataType === 'number' || columnDataType === 'string' || columnDataType === 'text'" class="re-vf-actions">
        <button class="re-btn re-btn-cancel" @click="clearValueFormatting">Clear Value Formatting</button>
      </div>
    </div>

  </b-modal>
</template>

<script>
import { ruleChainTypes } from '../../lib/rule-chain-types.js'
import { operatorsForDataType as operatorsFor, DISPLAY_ACTIONS, isBlankOperator } from '../../../../shared/display-rules.js'
import { formatFieldDisplay } from '../../../../shared/value-format.js'

const FORMATTING_PROPERTIES = DISPLAY_ACTIONS

let ruleIdCounter = 0

function generateRuleId () {
  return `rule_${Date.now()}_${++ruleIdCounter}`
}

function defaultNumberValueFormatting () {
  return {
    type: 'number',
    decimalPlaces: null,
    rounding: null,
    thousandsSeparator: null,
    decimalSeparator: '.',
    currency: null,
    percentMode: false
  }
}

function defaultTextValueFormatting () {
  return {
    type: 'text',
    maxLength: null,
    stripHtml: false,
    stripNonAlphanumeric: false,
    trimWhitespace: false,
    caseTransform: null,
    replacements: [],
    bold: false,
    italic: false,
    strike: false,
    fontFamily: null,
    fontSize: null
  }
}

export default {
  name: 'RuleEditor',
  props: {
    visible: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    valueFormatting: { type: Object, default: null },
    spreadsheet: { type: Object, default: null },
    allSpreadsheets: { type: Array, default: () => [] },
    columnUniqueTitle: { type: String, default: '' },
    columnDataType: { type: String, default: 'string' },
    applicationUniqueTitle: { type: String, default: '' }
  },
  data () {
    return {
      activeTab: 'visual',
      localVisible: false,
      localRules: [],
      localValueFormatting: {},
      isEditing: false,
      editingIndex: null,
      editType: 'if',
      editConditions: [],
      editActions: [],
      editMatchMode: 'all'
    }
  },
  computed: {
    directColumns () {
      if (!this.spreadsheet || !this.spreadsheet.columns) return []
      return this.spreadsheet.columns
    },
    relationshipColumns () {
      if (!this.spreadsheet || !this.spreadsheet.columns) return []
      return this.spreadsheet.columns.filter(c => c.type === 'relationship')
    },
    availableTypes () {
      return ruleChainTypes(this.localRules, this.editingIndex)
    },
    formattingPropertyOptions () {
      return FORMATTING_PROPERTIES
    },
    numberPreview () {
      const vf = this.localValueFormatting
      if (!vf || vf.type !== 'number') return ''
      const sample = vf.percentMode ? 0.75 : 1234567.89
      const display = formatFieldDisplay(sample, vf)
      return display ? display.text : ''
    },
    replacementRows () {
      if (!this.localValueFormatting.replacements) this.localValueFormatting.replacements = []
      return this.localValueFormatting.replacements
    }
  },
  watch: {
    visible (val) {
      this.localVisible = val
      if (val) {
        this.localRules = JSON.parse(JSON.stringify(this.rules || []))
        this.initValueFormatting()
        this.cancelEdit()
      }
    }
  },
  methods: {
    initValueFormatting () {
      const base = this.columnDataType === 'number' ? defaultNumberValueFormatting() : defaultTextValueFormatting()
      if (this.valueFormatting && this.valueFormatting.type) {
        this.localValueFormatting = Object.assign(base, JSON.parse(JSON.stringify(this.valueFormatting)))
        if (!Array.isArray(this.localValueFormatting.replacements)) this.localValueFormatting.replacements = []
      } else {
        this.localValueFormatting = base
      }
    },

    clearValueFormatting () {
      if (this.columnDataType === 'number') {
        this.localValueFormatting = defaultNumberValueFormatting()
      } else {
        this.localValueFormatting = defaultTextValueFormatting()
      }
    },

    operatorsForDataType (dataType) {
      return operatorsFor(dataType)
    },

    isNoValueOp (op) {
      return isBlankOperator(op)
    },

    regexpPlaceholder (operator) {
      if (operator === 'matches' || operator === 'notMatches') return 'regular expression'
      return 'value...'
    },

    getPropertyOptions (property) {
      const prop = FORMATTING_PROPERTIES.find(p => p.key === property)
      return prop && prop.options ? prop.options : []
    },

    getPropertyType (property) {
      const prop = FORMATTING_PROPERTIES.find(p => p.key === property)
      return prop ? prop.type : 'string'
    },

    getRelatedColumns (relColUniqueTitle) {
      const relCol = (this.spreadsheet?.columns || []).find(c => c.uniqueTitle === relColUniqueTitle)
      if (!relCol || !relCol.relatedSpreadSheet) return []
      const relSS = this.allSpreadsheets.find(s => s.uniqueTitle === relCol.relatedSpreadSheet)
      if (!relSS || !relSS.columns) return []
      return relSS.columns.filter(c => c.type !== 'relationship')
    },

    getColumnDataType (fieldName) {
      const systemTypeMap = { id: 'number', createdAt: 'datetime', updatedAt: 'datetime' }
      if (systemTypeMap[fieldName]) return systemTypeMap[fieldName]
      if (!this.spreadsheet || !this.spreadsheet.columns) return 'string'
      const col = this.spreadsheet.columns.find(c => c.uniqueTitle === fieldName)
      if (!col) return 'string'
      const typeMap = { text: 'string', number: 'number', dateTime: 'datetime', formula: 'string', password: 'string', richText: 'string', relationship: 'relationship', checkbox: 'checkbox', multiselect: 'string', address: 'string', duration: 'number', sequence: 'number', signature: 'signature' }
      return typeMap[col.type] || 'string'
    },

    getColumnForField (fieldName) {
      const systemColumns = [
        { uniqueTitle: 'id', title: 'ID', type: 'number', system: true },
        { uniqueTitle: 'createdAt', title: 'Created At', type: 'dateTime', system: true },
        { uniqueTitle: 'updatedAt', title: 'Updated At', type: 'dateTime', system: true }
      ]
      const systemCol = systemColumns.find(c => c.uniqueTitle === fieldName)
      if (systemCol) return systemCol
      if (!this.spreadsheet || !this.spreadsheet.columns) return null
      return this.spreadsheet.columns.find(c => c.uniqueTitle === fieldName) || null
    },

    condFieldIsRelationship (cond) {
      if (cond.dataType === 'relationship') return true
      if (cond.source === 'self') return this.columnDataType === 'relationship'
      if (cond.source === 'field') return this.getColumnDataType(cond.field) === 'relationship'
      return false
    },

    getCondRelatedSpreadSheet (cond) {
      if (cond.source === 'self') {
        const col = this.getColumnForField(this.columnUniqueTitle)
        return col ? col.relatedSpreadSheet : null
      }
      if (cond.source === 'field') {
        const col = this.getColumnForField(cond.field)
        return col ? col.relatedSpreadSheet : null
      }
      return null
    },

    onCondRelSearchInput (cond) {
      if (cond._relSearchTimeout) clearTimeout(cond._relSearchTimeout)
      cond._relSearchTimeout = setTimeout(() => this.searchCondRelRecords(cond), 250)
    },

    async searchCondRelRecords (cond) {
      const relSS = this.getCondRelatedSpreadSheet(cond)
      if (!relSS) return

      cond.relSearchLoading = true
      try {
        const headers = {}
        if (this.applicationUniqueTitle) {
          headers['application-unique-title'] = this.applicationUniqueTitle
        }
        const response = await fetch(
          `${VUE_APP_PROXY_SERVER_URL}/data/${relSS}/search?q=${encodeURIComponent(cond.relSearchQuery || '')}`,
          { headers }
        )
        const data = await response.json()
        const selectedIds = new Set((cond.relSelectedRecords || []).map(r => r.id))
        cond.relSearchResults = data.filter(r => !selectedIds.has(r.id))
      } catch (err) {
        console.error('relationship search error:', err)
        cond.relSearchResults = []
      } finally {
        cond.relSearchLoading = false
      }
    },

    selectCondRelRecord (cond, rec) {
      if (!cond.relSelectedRecords) cond.relSelectedRecords = []
      if (!cond.relSelectedRecords.find(r => r.id === rec.id)) {
        cond.relSelectedRecords.push({ id: rec.id, displayValue: rec.displayValue })
      }
      cond.relSearchResults = (cond.relSearchResults || []).filter(r => r.id !== rec.id)
      cond.relSearchQuery = ''
      // Store IDs as the condition value for evaluation
      cond.value = cond.relSelectedRecords.map(r => r.id)
    },

    removeCondRelRecord (cond, id) {
      cond.relSelectedRecords = (cond.relSelectedRecords || []).filter(r => r.id !== id)
      cond.value = cond.relSelectedRecords.map(r => r.id)
    },

    conditionSummary (cond) {
      let fieldLabel = 'this cell'
      if (cond.source === 'field') {
        fieldLabel = cond.field || '?'
      } else if (cond.source === 'related') {
        const q = cond.relatedQuantifier || 'any'
        const qLabel = q === 'none' ? 'none of' : q
        fieldLabel = `${qLabel} ${cond.relationshipColumn || '?'}.${cond.field || '?'}`
      }
      const opObj = this.operatorsForDataType(cond.dataType || 'string').find(o => o.key === cond.operator)
      const opLabel = opObj ? opObj.label : cond.operator
      if (this.isNoValueOp(cond.operator)) {
        return `${fieldLabel} ${opLabel}`
      }
      let displayValue = cond.value ?? ''
      if (this.condFieldIsRelationship(cond)) {
        const recs = cond.relSelectedRecords || []
        displayValue = recs.map(r => r.displayValue).join(', ') || '(none)'
      } else if (cond.dataType === 'checkbox') {
        displayValue = cond.value === 'true' ? 'Checked' : 'Unchecked'
      }
      return `${fieldLabel} ${opLabel} ${displayValue}`
    },

    actionSummary (action) {
      if (!action.property) return '?'
      const prop = FORMATTING_PROPERTIES.find(p => p.key === action.property)
      const label = prop ? prop.label : action.property
      if (prop && prop.type === 'boolean') {
        return action.value ? label : `No ${label}`
      }
      if (prop && prop.type === 'color') {
        return label
      }
      return `${label}: ${action.value}`
    },

    actionPillStyle (action) {
      const style = {}
      if (action.property === 'color' && action.value) {
        style.color = action.value
      }
      if (action.property === 'bgColor' && action.value) {
        style.backgroundColor = action.value
        style.color = '#fff'
      }
      if (action.property === 'bold' && action.value) {
        style.fontWeight = 'bold'
      }
      if (action.property === 'italic' && action.value) {
        style.fontStyle = 'italic'
      }
      if (action.property === 'underline' && action.value) {
        style.textDecoration = 'underline'
      }
      if (action.property === 'strikethrough' && action.value) {
        style.textDecoration = style.textDecoration ? `${style.textDecoration} line-through` : 'line-through'
      }
      if (action.property === 'fontFamily' && action.value) {
        style.fontFamily = action.value
      }
      return style
    },

    // Condition source changes
    onCondSourceChange (cond) {
      cond.field = null
      cond.relationshipColumn = null
      cond.relatedSpreadsheet = null
      cond.relatedQuantifier = 'any'
      if (cond.source === 'self') {
        cond.dataType = this.columnDataType
      }
    },

    onCondFieldChange (cond) {
      cond.dataType = this.getColumnDataType(cond.field)
      cond.operator = this.operatorsForDataType(cond.dataType)[0]?.key || '='
    },

    onCondRelColChange (cond) {
      const relCol = (this.spreadsheet?.columns || []).find(c => c.uniqueTitle === cond.relationshipColumn)
      if (relCol) {
        cond.relatedSpreadsheet = relCol.relatedSpreadSheet
      }
      cond.field = null
    },

    onActionPropertyChange (action) {
      const prop = FORMATTING_PROPERTIES.find(p => p.key === action.property)
      if (prop) {
        action.value = prop.default
      }
    },

    // Rule CRUD
    startAddRule () {
      this.isEditing = true
      this.editingIndex = null
      this.editType = this.availableTypes[0]
      this.editConditions = [this.newCondition()]
      this.editActions = [this.newAction()]
      this.editMatchMode = 'all'
    },

    editRule (idx) {
      const rule = this.localRules[idx]
      this.isEditing = true
      this.editingIndex = idx
      this.editType = rule.type
      this.editConditions = JSON.parse(JSON.stringify(rule.conditions || [])).map(c => ({
        ...c,
        relSearchQuery: '',
        relSearchResults: [],
        relSearchLoading: false,
        relSelectedRecords: c.relSelectedRecords || []
      }))
      this.editActions = JSON.parse(JSON.stringify(rule.actions || []))
      this.editMatchMode = rule.conditionMatchMode || 'all'
    },

    saveRule () {
      // Clean transient search state from conditions before saving
      const cleanConditions = (this.editType === 'else' ? [] : this.editConditions).map(c => {
        const clean = { ...c }
        delete clean.relSearchQuery
        delete clean.relSearchResults
        delete clean.relSearchLoading
        delete clean._relSearchTimeout
        return clean
      })
      const rule = {
        id: this.editingIndex !== null ? this.localRules[this.editingIndex].id : generateRuleId(),
        type: this.editType,
        conditions: cleanConditions,
        conditionMatchMode: this.editMatchMode,
        actions: this.editActions.filter(a => a.property)
      }

      if (this.editingIndex !== null) {
        this.localRules.splice(this.editingIndex, 1, rule)
      } else {
        // Insert else at end, elseif before else
        const lastIdx = this.localRules.length - 1
        if (lastIdx >= 0 && this.localRules[lastIdx].type === 'else' && rule.type !== 'else') {
          this.localRules.splice(lastIdx, 0, rule)
        } else {
          this.localRules.push(rule)
        }
      }

      this.cancelEdit()
    },

    removeRule (idx) {
      this.localRules.splice(idx, 1)
      if (this.editingIndex === idx) {
        this.cancelEdit()
      } else if (this.editingIndex !== null && this.editingIndex > idx) {
        this.editingIndex--
      }
    },

    cancelEdit () {
      this.isEditing = false
      this.editingIndex = null
      this.editConditions = []
      this.editActions = []
    },

    // Condition/Action helpers
    newCondition () {
      return {
        source: 'self',
        field: null,
        operator: this.operatorsForDataType(this.columnDataType)[0]?.key || '=',
        value: '',
        dataType: this.columnDataType,
        relationshipColumn: null,
        relatedSpreadsheet: null,
        relatedQuantifier: 'any',
        relSelectedRecords: [],
        relSearchQuery: '',
        relSearchResults: [],
        relSearchLoading: false
      }
    },

    addCondition () {
      this.editConditions.push(this.newCondition())
    },

    removeCondition (idx) {
      this.editConditions.splice(idx, 1)
    },

    newAction () {
      return { type: 'formatting', property: '', value: null }
    },

    addAction () {
      this.editActions.push(this.newAction())
    },

    removeAction (idx) {
      this.editActions.splice(idx, 1)
    },

    // Build the value formatting output (strip defaults/nulls for clean storage)
    buildValueFormattingOutput () {
      const vf = this.localValueFormatting
      if (!vf || !vf.type) return null

      // Check if anything is actually configured
      if (vf.type === 'number') {
        const d = defaultNumberValueFormatting()
        const hasChanges = Object.keys(d).some(k => {
          if (k === 'type') return false
          const left = vf[k]
          const right = d[k]
          if (Array.isArray(left) || Array.isArray(right)) return JSON.stringify(left || []) !== JSON.stringify(right || [])
          return (left == null ? null : left) !== (right == null ? null : right)
        })
        if (!hasChanges) return null
      } else if (vf.type === 'text') {
        const d = defaultTextValueFormatting()
        const hasChanges = Object.keys(d).some(k => {
          if (k === 'type') return false
          const left = vf[k]
          const right = d[k]
          if (Array.isArray(left) || Array.isArray(right)) return JSON.stringify(left || []) !== JSON.stringify(right || [])
          return (left == null ? null : left) !== (right == null ? null : right)
        })
        if (!hasChanges) return null
      }

      return { ...vf }
    },

    // Save/Cancel modal
    onSave () {
      this.$emit('update:rules', this.localRules)
      this.$emit('update:valueFormatting', this.buildValueFormattingOutput())
      this.$emit('update:visible', false)
    },

    onHide () {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
/* Tabs */
.re-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 12px;
  border-bottom: 2px solid #2e2e2b;
}
.re-tab {
  background: transparent;
  border: none;
  color: #7a7a78;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.15s;
}
.re-tab:hover {
  color: #b0b0ae;
}
.re-tab.active {
  color: #9ad12b;
  border-bottom-color: #9ad12b;
}

/* Container */
.re-container {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  background: #141412;
  padding: 12px;
}

/* Rule list */
.re-rule-list {
  margin-bottom: 10px;
}

.re-connector {
  display: flex;
  align-items: center;
  margin: 4px 0;
  padding: 0 4px;
}
.re-connector-line {
  flex: 1;
  height: 1px;
  background: #2e2e2b;
}

/* Rule item */
.re-rule-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #1a1a17;
  border: 1px solid #2e2e2b;
  border-radius: 5px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.12s;
}
.re-rule-item:hover {
  border-color: #474842;
  background: #1e1e1b;
}
.re-rule-open {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0;
  cursor: pointer;
}
.re-rule-item.re-rule-editing {
  border-color: #9ad12b;
}

/* Type badge */
.re-type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  height: 22px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  flex-shrink: 0;
  text-align: center;
  padding: 0 6px;
}
.re-type-if {
  background: #1e2e12;
  color: #9ad12b;
}
.re-type-elseif {
  background: #2e2512;
  color: #d0a05b;
}
.re-type-else {
  background: #251e30;
  color: #a05bd0;
}

/* Rule body */
.re-rule-body {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
}
.re-rule-conditions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.re-cond-text {
  color: #e8e8e6;
  font-size: 0.78rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
}
.re-cond-join {
  color: #a8a8a4;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.re-cond-empty {
  color: #a8a8a4;
  font-size: 0.75rem;
  font-style: italic;
}
.re-rule-arrow {
  color: #a8a8a4;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.re-rule-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.re-action-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  height: 20px;
  border-radius: 3px;
  font-size: 0.7rem;
  font-weight: 600;
  background: #1a2a3a;
  color: #5ba0d0;
  border: 1px solid #2a3a4a;
}
.re-action-empty {
  color: #a8a8a4;
  font-size: 0.75rem;
  font-style: italic;
}

/* Remove button */
.re-rule-remove, .re-cond-remove, .re-action-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: transparent;
  border: 1px solid transparent;
  color: #a8a8a4;
  font-size: 16px;
  line-height: 1;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.re-rule-remove:hover, .re-cond-remove:hover, .re-action-remove:hover {
  color: #f82471;
  border-color: #f82471;
  background: rgba(248, 36, 113, 0.1);
}

/* Empty state */
.re-empty {
  color: #a8a8a4;
  font-size: 0.82rem;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

/* Edit form */
.re-edit-form {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  background: #1a1a17;
  padding: 14px;
  margin-top: 10px;
}
.re-edit-title {
  color: #e8e8e6;
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 12px;
}

.re-form-row {
  margin-bottom: 10px;
}
.re-form-label {
  display: block;
  color: #7a7a78;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
}

.re-form-section {
  border: 1px solid #2e2e2b;
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  background: #141412;
}
.re-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.re-section-title {
  color: #b0b0ae;
  font-size: 0.78rem;
  font-weight: 600;
}

/* Match mode toggle */
.re-match-toggle {
  display: flex;
  gap: 4px;
}
.re-mt-btn {
  background: #1a1a17;
  border: 1px solid #2e2e2b;
  color: #7a7a78;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.06em;
}
.re-mt-btn:hover {
  border-color: #474842;
  color: #b0b0ae;
}
.re-mt-btn.active {
  background: #1e2e12;
  border-color: #9ad12b;
  color: #9ad12b;
}

/* Condition row */
.re-condition-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

/* Action row */
.re-action-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

/* Form controls */
.re-select {
  background: #1e1e1b;
  border: 1px solid #474842;
  color: #e8e8e6;
  font-size: 0.78rem;
  padding: 5px 8px;
  border-radius: 4px;
  outline: none;
  min-width: 100px;
}
.re-select:focus {
  border-color: #9ad12b;
  box-shadow: 0 0 0 1px rgba(154, 209, 43, 0.15);
}
.re-select-sm {
  padding: 3px 6px;
  font-size: 0.75rem;
}

.re-input {
  background: #1e1e1b;
  border: 1px solid #474842;
  color: #e8e8e6;
  font-size: 0.78rem;
  padding: 5px 8px;
  border-radius: 4px;
  outline: none;
  min-width: 80px;
}
.re-input:focus {
  border-color: #9ad12b;
  box-shadow: 0 0 0 1px rgba(154, 209, 43, 0.15);
}
.re-input-sm {
  padding: 3px 6px;
  font-size: 0.75rem;
}

.re-color-input {
  width: 36px;
  height: 28px;
  border: 1px solid #474842;
  border-radius: 4px;
  background: #1e1e1b;
  cursor: pointer;
  padding: 2px;
}

.re-checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #b0b0ae;
  font-size: 0.78rem;
  cursor: pointer;
}

.re-textarea {
  background: #1e1e1b;
  border: 1px solid #474842;
  color: #e8e8e6;
  font-size: 0.75rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  padding: 6px 8px;
  border-radius: 4px;
  outline: none;
  width: 100%;
  resize: vertical;
}
.re-textarea:focus {
  border-color: #9ad12b;
}

/* Add button */
.re-add-btn {
  color: #9ad12b;
  background: transparent;
  border: 1px dashed #474842;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
}
.re-add-btn:hover {
  color: #b8f046;
  background: #1e1e1b;
  border-color: #9ad12b;
}

.re-add-rule-row {
  margin-top: 8px;
}

/* Edit buttons */
.re-edit-buttons {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.re-btn {
  padding: 5px 16px;
  border-radius: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid;
}
.re-btn-save {
  background: #1e2e12;
  border-color: #9ad12b;
  color: #9ad12b;
}
.re-btn-save:hover {
  background: #2a3e1a;
}
.re-btn-cancel {
  background: transparent;
  border-color: #474842;
  color: #7a7a78;
}
.re-btn-cancel:hover {
  border-color: #6a6a67;
  color: #b0b0ae;
}

/* Quantifier selector */
.re-quantifier {
  min-width: 80px;
  font-weight: 600;
  color: #d0a05b;
}

/* Select option styling (for dark mode) */
.re-select option {
  background: #1e1e1b;
  color: #e8e8e6;
}

/* Relationship picker in conditions */
.re-rel-picker {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
}
.re-rel-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.re-rel-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #2a2a27;
  border: 1px solid #474842;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 0.72rem;
  color: #c8c8c6;
}
.re-rel-tag-remove {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0 2px;
  line-height: 1;
}
.re-rel-tag-remove:hover {
  color: #e44;
}
.re-rel-search-wrap {
  position: relative;
}
.re-rel-loading {
  font-size: 0.7rem;
  color: #888;
  padding: 2px 4px;
}
.re-rel-results {
  background: #1e1e1b;
  border: 1px solid #474842;
  border-radius: 4px;
  max-height: 120px;
  overflow-y: auto;
}
.re-rel-result-item {
  padding: 4px 8px;
  font-size: 0.75rem;
  color: #c8c8c6;
  cursor: pointer;
}
.re-rel-result-item:hover {
  background: #333;
  color: #9ad12b;
}

/* ═══ Value Formatting tab styles ═══ */
.re-vf-section-title {
  color: #b0b0ae;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #2e2e2b;
}
.re-vf-row {
  margin-bottom: 10px;
}
.re-vf-inline {
  display: flex;
  align-items: center;
  gap: 10px;
}
.re-vf-hint {
  color: #a8a8a4;
  font-size: 0.72rem;
  font-style: italic;
}
.re-vf-preview {
  margin-top: 14px;
  padding: 10px 12px;
  background: #1a1a17;
  border: 1px solid #2e2e2b;
  border-radius: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.re-vf-preview-label {
  color: #7a7a78;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.re-vf-preview-value {
  color: #e8e8e6;
  font-size: 1rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
}
.re-vf-actions {
  margin-top: 14px;
  display: flex;
  gap: 8px;
}
</style>
